import React, { useState } from "react";
import "./InputGroup3RadioButtonsText.css";
import { HelpCircle, AlertTriangle } from "lucide-react";

export interface RadioOption {
  value: string;
  label: string;
}

export interface InputGroup3RadioButtonsTextProps {
  label?: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  showHelpIcon?: boolean;
  showWarningIcon?: boolean;
  tagLabel?: string;
  disabled?: boolean;
  error?: string;
  errorMessage?: string;
  viewMode?: boolean;
  isMobile?: boolean;
  state?:
    | "enabled"
    | "hovered"
    | "selected"
    | "error"
    | "disabled"
    | "viewMode";
}

export const InputGroup3RadioButtonsText: React.FC<
  InputGroup3RadioButtonsTextProps
> = ({
  label = "",
  options = [],
  value = "",
  onChange,
  showHelpIcon = false,
  showWarningIcon = false,
  tagLabel = "",
  disabled = false,
  error = "",
  errorMessage = "",
  viewMode = false,
  isMobile = false,
  state = "enabled",
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  const handleOptionClick = (optionValue: string) => {
    if (disabled || viewMode) return;
    setSelectedValue(optionValue);
    onChange && onChange(optionValue);
  };

  const getStateClass = () => {
    if (viewMode)
      return isMobile
        ? "devicemobileStateviewMore"
        : "devicedesktopStateviewMor";
    if (error || state === "error")
      return isMobile ? "devicemobileStateerror" : "devicedesktopStateerror";
    if (disabled || state === "disabled")
      return isMobile
        ? "devicemobileStatedisabled"
        : "devicedesktopStatedisabled";
    if (selectedValue || state === "selected")
      return isMobile
        ? "devicemobileStateselected"
        : "devicedesktopStateselected";
    if (state === "hovered") return "devicedesktopStatehovered";
    return isMobile ? "devicemobileStateenabled" : "devicedesktopStateenabled";
  };

  if (viewMode) {
    const selectedOption = options.find(
      (option) => option.value === selectedValue
    );
    return (
      <div className="inputGroup3RadioButtonsText">
        {isMobile ? (
          <div className={getStateClass()}>
            <div className="title">
              <div className="errorMessage1">{label}</div>
            </div>
            <div className="details">
              <div className="loremIpsum28">
                {selectedOption?.label || "Lorem ipsum"}
              </div>
            </div>
          </div>
        ) : (
          <div className={getStateClass()}>
            <div className="label18">{label}</div>
            <div className="loremIpsum27">
              {selectedOption?.label || "Lorem ipsum"}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="inputGroup3RadioButtonsText">
      <div className={getStateClass()}>
        {isMobile ? (
          <>
            <div className="textIcons">
              <div className="label10">{label}</div>
              <div className="icons">
                {showHelpIcon && (
                  <div className="iconHelp">
                    <HelpCircle className="vectorIcon" />
                  </div>
                )}
                {showWarningIcon && (
                  <div className="iconWarning">
                    <AlertTriangle className="vectorIcon" />
                  </div>
                )}
                {tagLabel && (
                  <div className="tag5">
                    <div className="label1">{tagLabel}</div>
                  </div>
                )}
              </div>
            </div>
            <div
              className={
                error ? "optionsRow5" : disabled ? "optionsRow6" : "optionsRow5"
              }
            >
              {options.map((option) => {
                const isSelected = option.value === selectedValue;
                return (
                  <div
                    key={option.value}
                    className={
                      error
                        ? "radioButtonItem24"
                        : disabled
                          ? "radioButtonItem18"
                          : isSelected
                            ? "radioButtonItem23"
                            : "radioButtonItem15"
                    }
                    onClick={() => handleOptionClick(option.value)}
                  >
                    <div
                      className={isSelected ? "radioButtons5" : "radioButtons"}
                    >
                      <div className="icon">
                        <div
                          className={
                            isSelected
                              ? "iconChild2"
                              : disabled
                                ? "iconChild9"
                                : "iconChild"
                          }
                        />
                      </div>
                    </div>
                    <div className="loremIpsum">{option.label}</div>
                  </div>
                );
              })}
            </div>
            {error && (
              <div className="errorMessage2">
                <div className="errorMessage1">{errorMessage || error}</div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="label">{label}</div>
            {showHelpIcon && (
              <div className="iconHelp">
                <HelpCircle className="vectorIcon" />
              </div>
            )}
            {showWarningIcon && (
              <div className="iconWarning">
                <AlertTriangle className="vectorIcon" />
              </div>
            )}
            {tagLabel && (
              <div className="tag">
                <div className="label1">{tagLabel}</div>
              </div>
            )}
            <div className={disabled ? "optionsRow4" : "optionsRow"}>
              {options.map((option) => {
                const isSelected = option.value === selectedValue;
                return (
                  <div
                    key={option.value}
                    className={
                      error
                        ? "radioButtonItem"
                        : isSelected
                          ? "radioButtonItem5"
                          : state === "hovered" && option.value === "option3"
                            ? "radioButtonItem8"
                            : disabled
                              ? "radioButtonItem12"
                              : "radioButtonItem3"
                    }
                    onClick={() => handleOptionClick(option.value)}
                  >
                    <div
                      className={isSelected ? "radioButtons5" : "radioButtons"}
                    >
                      <div className="icon">
                        <div
                          className={
                            isSelected
                              ? "iconChild2"
                              : disabled
                                ? "iconChild9"
                                : "iconChild"
                          }
                        />
                      </div>
                    </div>
                    <div className="loremIpsum">{option.label}</div>
                  </div>
                );
              })}
            </div>
            {error && (
              <div className="errorMessage">
                <div className="errorMessage1">{errorMessage || error}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InputGroup3RadioButtonsText;
